import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { NavIcon, type IconName } from '../components/NavIcon.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '../lib/branding.tsx';

type NavGroup = 'overview' | 'operate' | 'configure' | 'monitor' | 'admin';
type NavRow = {
  to: string;
  label: string;
  icon: IconName;
  group: NavGroup;
  end?: boolean;
};

const NAV: NavRow[] = [
  { to: '/admin', label: 'Dashboard', icon: 'dashboard', group: 'overview', end: true },
  { to: '/admin/companies', label: 'Companies', icon: 'building', group: 'operate' },
  { to: '/admin/reviews', label: 'Reviews', icon: 'clipboard-check', group: 'operate' },
  { to: '/admin/questions', label: 'Question builder', icon: 'list-checks', group: 'configure' },
  { to: '/admin/documents', label: 'Documents', icon: 'file-text', group: 'configure' },
  { to: '/admin/activity', label: 'Activity', icon: 'activity', group: 'monitor' },
  { to: '/admin/users', label: 'Users', icon: 'users', group: 'admin' },
  { to: '/admin/settings', label: 'Settings', icon: 'settings', group: 'admin' },
];

const GROUP_LABEL: Record<NavGroup, string> = {
  overview: 'Overview',
  operate: 'Operate',
  configure: 'Configure',
  monitor: 'Monitor',
  admin: 'Admin',
};

type GroupedRow =
  | { kind: 'header'; key: string; label: string }
  | { kind: 'item'; key: string; row: NavRow };

function groupNav(rows: NavRow[]): GroupedRow[] {
  const out: GroupedRow[] = [];
  let last: NavGroup | null = null;
  for (const r of rows) {
    if (r.group !== last) {
      out.push({ kind: 'header', key: `h-${r.group}`, label: GROUP_LABEL[r.group] });
      last = r.group;
    }
    out.push({ kind: 'item', key: r.to, row: r });
  }
  return out;
}

function initials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || 'U';
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'U';
}

export function AdminShell() {
  const { user, loading, signOut } = useAdminAuth();
  const { branding } = useBranding();
  const location = useLocation();

  if (loading) return <CenteredSpinner />;
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  const brandName = branding.companyName?.replace(/ Safety.*$/i, '') ?? 'Locate2u';
  const eyebrow = brandName.toUpperCase();
  const rows = groupNav(NAV);

  return (
    <div className="flex min-h-screen bg-white">
      <aside
        className="relative flex w-72 shrink-0 flex-col border-r"
        style={{ background: '#FBFAF7', borderColor: '#E2E4EA' }}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-6 pb-5 pt-6">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]"
            style={{ background: 'var(--brand-primary)' }}
          >
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={brandName} className="h-5 w-auto" />
            ) : (
              <span className="text-sm font-bold text-white">
                {brandName.slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div
              className="font-mono-eyebrow text-[9.5px] tracking-[0.14em]"
              style={{ color: '#67789A' }}
            >
              {eyebrow} · INTERNAL
            </div>
            <div
              className="text-[15px] font-bold leading-tight tracking-tight"
              style={{ color: 'var(--brand-primary)' }}
            >
              Safety <span style={{ color: 'var(--brand-accent)' }}>&amp;</span> Compliance
            </div>
          </div>
        </div>

        {/* Search (presentational) */}
        <div
          className="mx-5 mb-4 flex items-center gap-2 rounded-lg border bg-white px-3 py-2"
          style={{ borderColor: '#E2E4EA' }}
        >
          <NavIcon name="search" size={14} style={{ color: '#67789A' }} />
          <input
            type="text"
            placeholder="Search…"
            className="flex-1 border-none bg-transparent text-[13px] outline-none placeholder:text-slate-400"
            style={{ color: '#0C1A2C' }}
          />
          <span
            className="font-mono-eyebrow rounded border px-1.5 py-[1px] text-[10px]"
            style={{ color: '#67789A', borderColor: '#E2E4EA', background: '#FBFAF7' }}
          >
            ⌘K
          </span>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-4 pb-2">
          {rows.map((row) => {
            if (row.kind === 'header') {
              return (
                <div
                  key={row.key}
                  className="font-mono-eyebrow px-3 pb-1.5 pt-4 text-[9.5px] tracking-[0.14em]"
                  style={{ color: '#67789A' }}
                >
                  {row.label.toUpperCase()}
                </div>
              );
            }
            const item = row.row;
            return (
              <NavLink
                key={row.key}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] transition ${
                    isActive ? 'font-semibold' : 'font-medium hover:bg-white'
                  }`
                }
                style={({ isActive }) =>
                  isActive
                    ? { background: '#FFF1E6', color: 'var(--brand-primary)' }
                    : { color: '#44587A' }
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute -left-4 top-1.5 bottom-1.5 w-[3px] rounded-r"
                        style={{ background: 'var(--brand-accent)' }}
                      />
                    )}
                    <NavIcon
                      name={item.icon}
                      size={17}
                      style={{ color: isActive ? 'var(--brand-primary)' : '#67789A' }}
                    />
                    <span className="flex-1 text-left">{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t px-4 pb-4 pt-3" style={{ borderColor: '#E2E4EA' }}>
          <div className="flex items-center gap-2 px-2 pb-3">
            <span
              className="h-[7px] w-[7px] rounded-full"
              style={{
                background: '#2DD27A',
                boxShadow: '0 0 8px rgba(45,210,122,0.5)',
              }}
            />
            <span className="flex-1 text-[11.5px]" style={{ color: '#44587A' }}>
              All systems normal
            </span>
          </div>
          <button
            type="button"
            onClick={() => void signOut()}
            className="flex w-full items-center gap-3 rounded-lg border bg-white p-2 text-left transition hover:bg-slate-50"
            style={{ borderColor: '#E2E4EA' }}
            title="Sign out"
          >
            <div
              className="font-mono-eyebrow flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
              style={{ background: 'var(--brand-primary)' }}
            >
              {initials(user.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="truncate text-[12.5px] font-semibold"
                style={{ color: '#0C1A2C' }}
              >
                {user.name}
              </div>
              <div className="truncate text-[10.5px]" style={{ color: '#67789A' }}>
                {user.email}
              </div>
            </div>
            <NavIcon name="log-out" size={16} style={{ color: '#67789A' }} />
          </button>
          <div
            className="font-mono-eyebrow mt-3 text-center text-[10px] uppercase tracking-[0.2em]"
            style={{ color: '#67789A' }}
          >
            Powered by Locate2u
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto" style={{ background: '#FBFAF7' }}>
        <div className="mx-auto max-w-6xl px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
